// @givmo/board — a new member's getting-started list, and "About the board" (CBG-YR4-START, -ACK's
// record-page line, -ABOUT; frames g1–g7 of design stop design-151).
//
// Every line is approved (CBG-YR4-COPY1's first part, Jonathan, 2026-10-15), and those that reuse an
// approved or shipped line say so. The list's name is "New member checklist" wherever it is met: the
// board's own list and wherever an officer meets it (CBG-UX6-COPY; design stop design-268's §4.5 and
// §9, frame g1, approved for building with every word a draft, D-CBG-35, 2026-10-22 23:09 PDT), and a
// member's own pages, with "checklist" for "list" in the editor and the close (the wayfinding copy
// round, every row as recommended, 2026-10-24 11:11 PDT). The words keep the module's discipline:
// each item states what is recorded and when; nothing says a document is required, nothing counts
// days, nothing names anyone as behind, and no line is about giving or raising money (D-CBG-4).
//
// CBG-UX-COPY2 (D-CBG-42, Jonathan, 2026-10-24 19:08 PDT, and its amendment of 21:54 PDT): the yearly
// form is the "conflicts of interest form" in every line that named the questionnaire or the annual
// disclosure, "fill out" where it was answered; a member's own act on a step reads "I’ve done this"
// (A5d), and their closed checklist says nothing more is asked of them (A7).

export const onboardingWords = {
  // ── The officer's card on a member's page (g1) ────────────────────────────────────────────────
  // Not drawn: design-268 §9's name for the card's title.
  cardTitle: 'New member checklist',
  // Not drawn.
  cardLead: 'From your board’s new member checklist.',
  howLines: [
    'Each document is confirmed by the member in Board, with a passkey or an authenticator app, or recorded by you from a signed paper form.',
    'A confirmation names the exact version read. When the board adopts a new version, it’s a new item to confirm.',
    'Nothing on this list changes what the member can read, how they vote or whether they count toward a quorum.',
  ],
  // Statuses, as plain words.
  confirmedOn: (day: string) => `Confirmed ${day}`,
  paperOn: (day: string) => `Recorded from a paper form signed ${day}`,
  annualOn: (label: string) => `Confirmed in the ${label} conflicts of interest form`,
  notYet: 'Not yet',
  doneOn: (day: string) => `Done ${day}`,
  unavailable: 'No version in force',
  // Row acts (officer).
  recordPaperForm: 'Record a signed paper form',
  // The officer marks one of the board's steps done for the member; the member's own act is
  // `markOwnStepDone`, below.
  markDone: 'Mark done',
  undoDone: 'Undo',
  chooseBuddy: 'Choose a board buddy',
  // Drawn g1 (the board's checklist); not drawn on a member's own list.
  changeList: 'Change the checklist',
  // The disclosure item, read from the year's conflicts of interest forms (CBG-YR2).
  disclosureTitle: 'First conflicts of interest form',
  disclosureSentNotAnswered: (label: string, sent: string) =>
    `The ${label} conflicts of interest form, sent ${sent} · not filled out yet`,
  disclosureNotSent: (label: string) =>
    `The ${label} conflicts of interest form hasn’t been sent yet`,
  disclosureAnswered: (label: string, day: string) =>
    `The ${label} conflicts of interest form, filled out ${day}`,
  disclosureNextYear: 'With the board’s next conflicts of interest forms',
  // A button, five words as before: the item above it names the form.
  sendQuestionnaire: (label: string) => `Send the ${label} form`,
  sendQuestionnaireNamed: (label: string) => `Send the ${label} conflicts of interest form`,
  // The buddy item.
  buddyTitle: 'Board buddy',
  buddyNone: 'Not chosen yet',
  versionLine: (day: string) => `The version adopted ${day}`,
  recordLine: (day: string) => `From the minute book · ${day}`,
  // States no frame drew: no list yet, and a closed list.
  // Not drawn.
  noListYet: 'No new member checklist yet.',
  // Not drawn: design-268 §9's words.
  startList: 'Start a new member checklist',
  // Not drawn.
  boardListNotChosen: 'Your board hasn’t chosen its new member checklist yet.',
  chooseBoardList: 'Choose it',
  closedLine: (day: string) => `Closed ${day}. What it records stays.`,
  closeList: 'Close this checklist',

  // ── The close, behind a confirming dialog (C16) ───────────────────────────────────────────────
  // Not drawn.
  closeTitle: (firstName: string) => `Close ${firstName}’s new member checklist?`,
  closeBody:
    'You can’t open it again. Who confirmed what, and when, stays on the records either way.',
  closeConfirm: 'Close the checklist',
  cancel: 'Cancel',

  // ── Recording a signed paper form ─────────────────────────────────────────────────────────────
  paperTitle: 'Record a signed paper form',
  paperLead: (name: string) =>
    `Choose the documents the signed form covers. Each is recorded as confirmed by ${name}, in the version shown, as entered by you from a paper form.`,
  paperDate: 'The date on the form',
  paperNote: 'Where the signed form is',
  paperNoteHint: 'Kept with the record — for example, filed with the secretary.',
  paperNothingToRecord: 'Every document on this list is confirmed already.',
  paperRecord: 'Record',
  paperDone: 'Recorded from the signed paper form.',

  // ── Choosing a buddy ──────────────────────────────────────────────────────────────────────────
  buddyDialogTitle: (firstName: string) => `Choose a board buddy for ${firstName}`,
  buddyDialogLead: (firstName: string) =>
    `Someone on the board ${firstName} can ask about how it works. Board sends them nothing.`,
  buddyField: 'Board buddy',
  buddyPlaceholder: 'Choose someone',
  buddyChoose: 'Choose',

  // ── Changing a list (the board's, or one person's) ────────────────────────────────────────────
  // Not drawn: the editor "Change the checklist" opens (g1).
  editTitleBoard: 'Change your board’s new member checklist',
  editTitleMember: (firstName: string) => `Change ${firstName}’s checklist`,
  editLeadBoard:
    'Members added to the board from now on get this checklist. Checklists already started stay as they are.',
  editLeadMember: 'Only this person’s checklist changes.',
  rowActions: (title: string) => `Actions for ${title}`,
  moveUp: 'Move up',
  moveDown: 'Move down',
  remove: 'Remove',
  addDocument: 'Add a document',
  addDocumentPlaceholder: 'Choose a document',
  addStep: 'Add a step',
  stepPlaceholder: 'For example: tour the office with the executive director',
  add: 'Add',
  save: 'Save',
  documentsFailed: 'We couldn’t load your board’s documents. Please try again.',

  // ── Board members › New member checklist (g2; design-268's g1, the board's own list) ──────────
  membersTab: 'Members',
  // Drawn g1: the tab.
  listTab: 'New member checklist',
  // Drawn g1: who the checklist is for, and what using one does. The director's to-do list leads with
  // it (CBG-UX7-WEB-DIR), so it says what g1 drew; "to the board", since a committee-only seat gets
  // none (5.10).
  listLead:
    'What each new board member finds first on their to-do list: what to read and what to do. Once you use a checklist, everyone you add to the board gets their own copy, and you can change one person’s on their page. Members already on the board don’t get one automatically.',
  // Drawn g1: the starting card's title.
  startingTitle: 'A starting checklist, from what your board has recorded',
  // Approved D-CBG-41 (Jonathan, 2026-10-24 15:08 PDT), drawn g1: its line, "and two steps" dropped
  // while the board buddy is dark (CBG-YR4-BUDDY-DARK), since the list then holds one step at most.
  startingLine:
    'Your governing documents in force. Nothing is used until you choose it, and you can change it at any time.',
  // Not drawn: the card once the board uses its checklist.
  adoptedTitle: 'Your board’s new member checklist',
  adoptedLine: (day: string, name: string | null) =>
    name ? `Adopted ${day} by ${name}.` : `Adopted ${day}.`,
  // Drawn g1.
  adoptList: 'Use this checklist',
  readGroup: 'To read and confirm',
  doGroup: 'To do',
  inForceSource:
    'From your governing documents: each one in the version in force when the member reads it.',
  disclosureSource: 'With the board’s yearly conflicts of interest forms',
  buddySource: 'An officer chooses someone on the board',
  stepSource: 'Your board’s own step',
  adoptedOn: (day: string) => `Adopted ${day}`,

  // ── The director's home row (g4) ──────────────────────────────────────────────────────────────
  attentionRow: (count: number) =>
    `New member checklist · ${count === 1 ? '1 thing' : `${count} things`} to read`,

  // ── The director's own page (g5) ──────────────────────────────────────────────────────────────
  directorTitle: 'New member checklist',
  aboutCardTitle: 'About the board',
  aboutCardLine: 'Who serves, how meetings work, and the board’s recent decisions.',
  open: 'Open',
  read: 'Read',
  toReadGroup: 'To read',
  toReadLine: 'Open each one, then record that you’ve read it.',
  toDoGroup: 'To do',
  yourDisclosure: (label: string) => `Your ${label} conflicts of interest form`,
  dueOn: (day: string) => `Due ${day}`,
  // Approved in CBG-UX-COPY3 (D9): the form's row before the board has sent it, so there is nothing
  // to open and no date to run against; once sent, the row keeps "Due {day}" and its Open.
  notSentToYouYet: 'Not sent to you yet',
  yourBuddyLine: (name: string, capacity: string | null) =>
    `Your board buddy is ${name}${capacity ? `, ${capacity}` : ''}.`,
  noOwnList: 'Your board hasn’t started a new member checklist for you.',
  ownListClosed: 'Your board closed this checklist. Nothing more is asked of you here.',
  // The member's own act on a step, on their checklist page and their to-do list (A5d).
  markOwnStepDone: 'I’ve done this',

  // ── The record page, reached from the list (g6) ───────────────────────────────────────────────
  askedToConfirm: 'Your board asks new members to confirm they’ve read this.',
  backToList: 'Back to your checklist',
  // The record read's own acknowledgment, on a reload (contract §2.4; not drawn).
  youConfirmedOn: (day: string) => `You confirmed you’ve read this on ${day}.`,
  paperConfirmedOn: (day: string) =>
    `Recorded from a signed paper form, signed ${day}, as confirming you’ve read this.`,

  // ── About the board (g7) ──────────────────────────────────────────────────────────────────────
  aboutTitle: 'About the board',
  aboutLine: 'From your board’s own records. Everything here is also in the minute book.',
  whoServes: 'Who serves',
  you: 'you',
  whoDoesWhat: 'Who does what',
  dutiesAdopted: (day: string) => `As your board recorded its officers’ duties on ${day}.`,
  howMeetingsWork: 'How meetings work',
  documents: 'Governing documents',
  recentDecisions: 'Recent decisions',
  recentLine: 'Minutes and written consents from the last twelve months.',
  decidedOn: (kind: 'minutes' | 'written_consent', day: string) =>
    kind === 'written_consent' ? `Written consent · executed ${day}` : `Minutes · approved ${day}`,
  nextMeeting: 'Next meeting',
  nothingRecorded: 'Nothing recorded yet.',
} as const;
